import React, { useCallback, useState } from 'react';

import { Confirm, Icons, Modal } from '@components/common';
import Actions from '@components/common/Actions';

import { useMutateRelinkCrowdCell } from '@hooks/crowdCells/useMutateRelinkCrowdCell';
import { useMutateUnlinkCrowdCell } from '@hooks/crowdCells/useMutateUnlinkCrowdCell';
import { useQueryGNodeBConfigParameters } from '@hooks/crowdCells/useQueryGNodeBConfigParameters';

import { ICrowdCellProfile } from '@interfaces/crowdcell/crowdCells';

import { ECrowdCellConnectionType } from '@enums/common';

import { MODAL_WIDTH_455, MODAL_WIDTH_480 } from '@constants/modals';
import { crowdCellModalUnlinkConfirm } from '../constants';

import EditConfiguration from './EditConfiguration';
import LinkCrowdCellForm from './LinkCrowdCellForm';

import styles from './LimenetProfile.module.scss';

interface ILinkedInformationProps {
	crowdCell: ICrowdCellProfile;
	isEditAccess: boolean;
	siteDbId: string;
}

const LinkedCrowdCellInfo = ({
	crowdCell,
	isEditAccess,
	siteDbId,
}: ILinkedInformationProps) => {
	const { status, id, firstSeenText, lastSeenText } = crowdCell;

	const {
		gNodeBConfigParameters,
		frequency,
		isRestart,
		isLoadingGNodeBConfigParameters,
	} = useQueryGNodeBConfigParameters({ id });

	const classNameStatus = status.name !== ECrowdCellConnectionType.OFFLINE ? styles.online : styles.offline;

	const [isShownUnlinkModal, setIsShownUnlinkModal] = useState<boolean>(false);
	const [isShownRelinkCrowdCell, setIsShownRelinkModal] = useState<boolean>(false);
	const [isShownEditConfigurationModal, setIsShownEditConfigurationModal] = useState<boolean>(false);
	const [isCancelEditConfigConfirmationModalOpen, setIsCancelEditConfigConfirmationModalOpen] = useState(false);
	const [isDirty, setIsDirty] = useState(false);

	const openUnlinkConfirm = () => setIsShownUnlinkModal((prevState) => !prevState);
	const openRelinkConfirm = () => setIsShownRelinkModal((prevState) => !prevState);

	const closeUnlinkModal = () => setIsShownUnlinkModal(false);
	const closeRelinkModal = () => setIsShownRelinkModal(false);

	const closeCancelEditConfigConfirmationModal = () => setIsCancelEditConfigConfirmationModalOpen(false);

	const openEditConfigurationModal = () => setIsShownEditConfigurationModal(true);
	const closeEditConfigurationModal = useCallback(() => setIsShownEditConfigurationModal(false), []);

	const openCancelEditConfigConfirmationModal = useCallback((isWithoutConfirm?: boolean) => {
		if (!isDirty || isWithoutConfirm) {
			closeEditConfigurationModal();

			return;
		}

		setIsCancelEditConfigConfirmationModalOpen(true);
	}, [closeEditConfigurationModal, isDirty]);

	const { handleRelinkCrowdCell, isLoadingRelinkCrowdCell } = useMutateRelinkCrowdCell([closeRelinkModal]);
	const { handleUnlinkCrowdCell } = useMutateUnlinkCrowdCell([closeUnlinkModal]);

	const unlinkCrowdCellHandler = () => handleUnlinkCrowdCell({ crowdCellId: id, siteDbId });

	const relinkCrowdCellHandler = (newCrowdCellId: string) =>
		handleRelinkCrowdCell({ crowdCellId: id, siteDbId, newCrowdCellId });

	const onConfirmCancelEditConfigForm = useCallback(() => {
		closeCancelEditConfigConfirmationModal();
		closeEditConfigurationModal();
		setIsDirty(false);
	}, [closeEditConfigurationModal]);

	return (
		<>
			<Modal
				isOpen={isShownUnlinkModal}
				onClose={closeUnlinkModal}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onClose={closeUnlinkModal}
					onSubmit={unlinkCrowdCellHandler}
					{...crowdCellModalUnlinkConfirm}
				/>
			</Modal>

			<Modal
				isOpen={isShownRelinkCrowdCell}
				onClose={closeRelinkModal}
				title='Link Flex-5G'
				width={MODAL_WIDTH_455}
			>
				<LinkCrowdCellForm
					closeModal={closeRelinkModal}
					isLoadingRelinkForm={isLoadingRelinkCrowdCell}
					submitHandler={relinkCrowdCellHandler}
					toggleLinkModal={closeRelinkModal}
				/>
			</Modal>

			<Modal
				isOpen={isShownEditConfigurationModal}
				onClose={openCancelEditConfigConfirmationModal}
				title='List of editable values of the parameters'
				width='800px'
			>
				<EditConfiguration
					closeEditConfigurationModal={openCancelEditConfigConfirmationModal}
					crowdCellId={id}
					frequency={frequency?.toString() || ''}
					gNodeBConfigParameters={gNodeBConfigParameters}
					isLoadingGNodeBConfigParameters={isLoadingGNodeBConfigParameters}
					isRestart={!!isRestart}
					setIsDirty={setIsDirty}
				/>
			</Modal>

			<Modal
				isOpen={isCancelEditConfigConfirmationModalOpen}
				onClose={closeCancelEditConfigConfirmationModal}
				title='Confirm action'
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onSubmit={onConfirmCancelEditConfigForm}
					question='You are about to close the form.'
				>
					Are you sure you want to leave without saving?
				</Confirm>
			</Modal>
			<div className={styles.crowdCellInfoHeader}>
				<div className={styles.icon}>{Icons.manageSiteDB()}</div>
				<div className={styles.crowdCellId}>
					Flex-5G: <span>{crowdCell.systemId}</span>
				</div>
				<Actions
					editHandler={openRelinkConfirm}
					editTooltip='Relink Flex-5G'
					gridNumberOfColumns={1}
					isEdit
					isEditDisable={!isEditAccess}
				/>
				<Actions
					configureHandler={openEditConfigurationModal}
					configureTooltip='gnodeb.cfg'
					gridNumberOfColumns={1}
					isConfigure
				/>
				<Actions
					gridNumberOfColumns={1}
					isLink
					isLinkDisable={!isEditAccess}
					linkHandler={openUnlinkConfirm}
					linkTooltip='Unlink from the Flex-5G'
					linkedToSite
				/>
			</div>
			<div className={styles.crowdCellInfo}>
				<div className={styles.crowdCellInfoRow}>
					<div className={styles.crowdCellInfoItem}>
						Status:&nbsp;
						<span className={classNameStatus}>{status.title}</span>
					</div>
					<div className={styles.crowdCellInfoItem}>
						First seen:&nbsp;<span>{firstSeenText}</span>
					</div>
					<div className={styles.crowdCellInfoItem}>
						Last seen:&nbsp;<span>{lastSeenText}</span>
					</div>
				</div>
			</div>
		</>
	);
};

export default LinkedCrowdCellInfo;
